import React from 'react';
import classNames from 'classnames';

import { ResponsiveImage } from '../../components/';

import delowoLogo from '../../assets/brand/delowo-logo.svg';

import css from './Logo.module.css';

// Intrinsic size of delowo-logo.svg (viewBox 978.22 × 192). Used for width/height attributes so the
// browser reserves the aspect ratio before the SVG loads (no layout shift); CSS sets the rendered height.
const BRAND_LOGO_WIDTH = 978;
const BRAND_LOGO_HEIGHT = 192;

const HEIGHT_24 = 24;
const HEIGHT_36 = 36;
const HEIGHT_48 = 48;
const HEIGHT_OPTIONS = [HEIGHT_24, HEIGHT_36, HEIGHT_48];

// logoSettings property supports only 3 types at this point
const isValidLogoSettings = settings =>
  settings?.format === 'image' && HEIGHT_OPTIONS.includes(settings?.height);
const isImageAsset = logo => logo?.type === 'imageAsset';

// Each type can have multiple image variants
const getVariantNames = variantsObj => {
  return Object.keys(variantsObj) || [];
};

// Variant data contains width and height among other things
// The width is needed for sizes attribute of responsive logo imgs
const getVariantData = variants => {
  // This assume that "scaled" variant exists
  // If other variants are introduced, this setup might need some tuning.
  return variants['scaled'];
};

// We have maximum heights for each logo type. It's enforced through classes
const getHeightClassName = height => {
  return height === HEIGHT_48 ? css.logo48 : height === HEIGHT_36 ? css.logo36 : css.logo24;
};

export const LogoComponent = props => {
  const {
    className,
    logoImageClassName,
    layout,
    marketplaceName,
    logoImageDesktop,
    logoImageMobile,
    logoSettings,
    ...rest
  } = props;

  const hasValidLogoSettings = isValidLogoSettings(logoSettings);
  const logoClasses = className || css.root;
  const logoImageClasses = classNames(
    logoImageClassName || css.logo,
    getHeightClassName(logoSettings?.height)
  );

  // Logo from hosted asset
  if (isImageAsset(logoImageDesktop) && hasValidLogoSettings && layout === 'desktop') {
    const variants = logoImageDesktop.attributes.variants;
    const variantNames = getVariantNames(variants);
    const { width } = getVariantData(variants);
    return (
      <div className={logoClasses} style={{ width: `${width}px` }}>
        <ResponsiveImage
          rootClassName={logoImageClasses}
          alt={marketplaceName}
          image={logoImageDesktop}
          variants={variantNames}
          sizes={`${width}px`}
          width={width}
          height={logoSettings?.height}
        />
      </div>
    );
  } else if (isImageAsset(logoImageMobile) && hasValidLogoSettings && layout === 'mobile') {
    const variants = logoImageMobile.attributes.variants;
    const variantNames = getVariantNames(variants);
    const { width } = getVariantData(variants);

    // Sizes on small screens are mainly limited by space: side buttons take 2x66 px, the rest is for logo.
    // If logo's (1x) width is less than 188, we can use logo's width as limit for sizes attribute
    // On general case, up to the screen size of 500px, we could say that the logo _might_ take all the available space
    // However, after 500px, the max aspect ratio for the logo should start limiting the logo's width.
    const sizes =
      width <= 188 ? `${width}px` : `(max-width: 500px) calc(100vw - 132px), ${width}px`;
    return (
      <div className={logoClasses}>
        <ResponsiveImage
          rootClassName={logoImageClasses}
          alt={marketplaceName}
          image={logoImageMobile}
          variants={variantNames}
          sizes={sizes}
          width={width}
        />
      </div>
    );
  } else if (layout === 'desktop') {
    return (
      <div className={logoClasses}>
        <img className={logoImageClasses} src={logoImageDesktop} alt={marketplaceName} {...rest} />
      </div>
    );
  }

  return (
    <div className={logoClasses}>
      <img className={logoImageClasses} src={logoImageMobile} alt={marketplaceName} {...rest} />
    </div>
  );
};

/**
 * This component returns a logo
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {('desktop' | 'mobile')} props.layout
 * @param {string?} props.alt alt text for logo image
 * @returns {JSX.Element} logo component
 */
const Logo = props => {
  const { layout = 'desktop', className, logoImageClassName, alt, ...rest } = props;

  // Delowo brand wordmark (D7) is rendered from the version-controlled SVG asset.
  // The hosted Console logo (config.branding.logoImageDesktop/Mobile + logoSettings) is still
  // loaded by configHelpers and only used for emails / the web manifest. To go back to the
  // Console logo, render <LogoComponent> here with those config.branding values (see git history).
  return (
    <div className={className || css.root}>
      <img
        className={classNames(
          css.brandLogo,
          layout === 'mobile' ? css.brandLogoMobile : css.brandLogoDesktop,
          logoImageClassName
        )}
        src={delowoLogo}
        alt="Delowo"
        width={BRAND_LOGO_WIDTH}
        height={BRAND_LOGO_HEIGHT}
        {...rest}
      />
    </div>
  );
};

export default Logo;
